import { FC, useContext } from 'react';

import { useQuery } from '@apollo/client';
import { Label, Preloader, Tooltip, Separator } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { ClientsAvatar } from 'components';
import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { formatFIO, formatPhone } from 'utils';

import {
  IGetWorkshiftWorkerRequest,
  IGetWorkshiftWorkerResponse,
  IWorkerBlockProps,
} from './interfaces';
import { GET_WORKSHIFT_WORKER } from './schema';
import { getFormattedDate } from './utils';
import s from './WorkerBlock.module.scss';

export const WorkerBlock: FC<IWorkerBlockProps> = ({
  workShiftId,
  isLoading,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const {
    state: {
      settings: {
        currency: { sign },
      },
    },
  } = useContext(SettingsContext);

  const { data: workshiftMoneyData } = useQuery<
    IGetWorkshiftWorkerResponse,
    IGetWorkshiftWorkerRequest
  >(GET_WORKSHIFT_WORKER, {
    fetchPolicy: 'cache-only',
    variables: { id: workShiftId },
    skip: !permissions?.has('work_shift_active'),
  });

  const { created_at, worker, finished_at, sum } =
    workshiftMoneyData?.getWorkShiftPaymentOverviewData ?? {};

  return (
    <div className={s.container}>
      <FeatureHOC alias="tips">
        {Boolean(sum?.tips) && (
          <div className={s.tips}>
            <Tooltip
              content={t('work_shift.tips')}
              contentClassName={s.tooltip}
              position={'bottom-left'}
            >
              <Label
                iconName="HandCoinIcon"
                text={`${sum?.tips || 0} ${sign}`}
                color="green"
                size="s"
              />
            </Tooltip>
          </div>
        )}
      </FeatureHOC>
      <div className={s.block}>
        <Preloader loading={isLoading} small className={s.preloader} />
        <ClientsAvatar avatar={worker?.avatar_url} roles={worker?.roles} />
        <div className={s.worker}>
          {worker ? formatFIO(worker) : '—'}
          <span className={s.phone}>
            {worker?.phone ? formatPhone(worker.phone) : '—'}
          </span>
        </div>
      </div>
      <Separator className={s.separator} />
      <div className={s.labels}>
        <Label
          iconName={created_at ? 'StartIcon' : undefined}
          text={
            created_at
              ? getFormattedDate(created_at)
              : t('workShifts.status.no')
          }
          color="gray-secondary"
          rounded
        />
        {finished_at && (
          <Label
            iconName="FinishIcon"
            text={getFormattedDate(finished_at)}
            color="gray-secondary"
            rounded
          />
        )}
      </div>
    </div>
  );
};
